/**
 * Cart drawer CRO: add-to-cart popup, first-add drawer opening, upsells and one-tap adds.
 */

/* global theme, trapFocus */

(() => {
  const FIRST_ADD_KEY = 'cro:first-atc-drawer-shown';
  const UPSELLS_TO_SHOW = 2;

  const session = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (err) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (err) {
        // Storage unavailable (e.g. private mode) - behaves as if every add is the first.
      }
    }
  };

  /**
   * Public helpers used by product-form.js.
   */
  window.CartDrawerCro = {
    /**
     * Whether this add to cart should open the drawer (first add in the browsing session).
     * @returns {boolean}
     */
    shouldOpenDrawer() {
      return !session.get(FIRST_ADD_KEY);
    },

    markDrawerOpened() {
      session.set(FIRST_ADD_KEY, '1');
    },

    /**
     * Shows the "Added to cart" popup.
     * @param {string} productTitle - Title of the product that was added.
     */
    showToast(productTitle) {
      const toast = document.querySelector('cart-toast');
      if (toast) toast.show(productTitle);
    }
  };

  if (!customElements.get('cart-toast')) {
    class CartToast extends HTMLElement {
      connectedCallback() {
        this.productEl = this.querySelector('.js-cro-toast-product');
        this.duration = (Number(this.dataset.duration) || 4) * 1000;

        this.querySelector('.js-cro-toast-show-cart').addEventListener('click', () => {
          this.hide();
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
        });
        this.querySelector('.js-cro-toast-close').addEventListener('click', () => this.hide());

        // Keep the popup visible while it's being interacted with.
        this.addEventListener('mouseenter', () => clearTimeout(this.timer));
        this.addEventListener('mouseleave', () => this.startTimer());
        this.addEventListener('focusin', () => clearTimeout(this.timer));
        this.addEventListener('focusout', () => this.startTimer());
      }

      /**
       * @param {string} productTitle - Title of the product that was added.
       */
      show(productTitle) {
        this.productEl.textContent = productTitle || '';
        this.hidden = false;
        // Force a reflow so the transition runs when re-shown.
        this.getBoundingClientRect();
        this.classList.add('is-visible');
        this.startTimer();
      }

      hide() {
        clearTimeout(this.timer);
        this.classList.remove('is-visible');
        setTimeout(() => {
          if (!this.classList.contains('is-visible')) this.hidden = true;
        }, 300);
      }

      startTimer() {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.hide(), this.duration);
      }
    }

    customElements.define('cart-toast', CartToast);
  }

  if (!customElements.get('cart-upsells')) {
    class CartUpsells extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('.js-cro-upsells-list');
        this.heading = this.querySelector('.js-cro-upsells-heading');
        this.gap = Number(this.dataset.gap) || 0;
        this.cartIds = (this.dataset.cartIds || '').split(',').filter(Boolean);

        this.render();
        if (this.needsRecommendations()) this.loadRecommendations();
      }

      getCards() {
        return Array.from(this.list.querySelectorAll('.cro-card'));
      }

      unlocksFreeDelivery(card) {
        return this.gap > 0 && Number(card.dataset.price) >= this.gap;
      }

      /**
       * Recommendations API (source b) is only needed when there aren't 2 bought-together
       * products, or when nothing in the pool would unlock free delivery.
       * @returns {boolean}
       */
      needsRecommendations() {
        const cards = this.getCards();
        const boughtTogether = cards.filter((card) => card.dataset.source === 'a').length;
        return boughtTogether < UPSELLS_TO_SHOW
          || (this.gap > 0 && !cards.some((card) => this.unlocksFreeDelivery(card)));
      }

      /**
       * Fetches Shopify recommendations (complementary, then related) for the most recently
       * added cart item and inserts them after the bought-together products.
       */
      async loadRecommendations() {
        const productId = this.dataset.productId;
        const baseUrl = this.dataset.recommendationsUrl;
        if (!productId || !baseUrl) return;

        const existing = new Set(this.getCards().map((card) => card.dataset.productId));
        const found = [];

        for (const intent of ['complementary', 'related']) {
          try {
            // eslint-disable-next-line no-await-in-loop
            const response = await fetch(`${baseUrl}?product_id=${productId}&limit=6&intent=${intent}&section_id=cart-drawer-recommendations`);
            if (!response.ok) continue;
            // eslint-disable-next-line no-await-in-loop
            const html = await response.text();
            const tmpl = document.createElement('template');
            tmpl.innerHTML = html;

            tmpl.content.querySelectorAll('.cro-card').forEach((card) => {
              const id = card.dataset.productId;
              if (existing.has(id) || this.cartIds.includes(id)) return;
              existing.add(id);
              found.push(card);
            });
          } catch (err) {
            // Network error - fall through to the next source.
          }

          if (found.length >= UPSELLS_TO_SHOW) break;
        }

        if (!found.length || !this.isConnected) return;

        const firstCategoryCard = this.list.querySelector('.cro-card[data-source="c"]');
        found.forEach((card) => this.list.insertBefore(document.importNode(card, true), firstCategoryCard));
        this.render();
      }

      /**
       * Ranks the candidates and shows the top 2. Source order (a, b, c) is kept, except that
       * when the cart is under the free delivery threshold, products that unlock it come first.
       */
      render() {
        const cards = this.getCards();
        const ranked = this.gap > 0
          ? [
            ...cards.filter((card) => this.unlocksFreeDelivery(card)),
            ...cards.filter((card) => !this.unlocksFreeDelivery(card))
          ]
          : cards;

        let showsBadge = false;
        ranked.forEach((card, index) => {
          const visible = index < UPSELLS_TO_SHOW;
          const unlocks = visible && this.unlocksFreeDelivery(card);
          const badge = card.querySelector('.cro-card__badge');

          card.hidden = !visible;
          if (badge) {
            badge.textContent = this.dataset.badge;
            badge.hidden = !unlocks;
          }
          if (unlocks) showsBadge = true;

          // Keep DOM order equal to the display order.
          this.list.appendChild(card);
        });

        this.heading.textContent = showsBadge ? this.dataset.headingGap : this.dataset.heading;
        this.hidden = cards.length === 0;
      }
    }

    customElements.define('cart-upsells', CartUpsells);
  }

  /**
   * One-tap "+ Add" for upsells and best sellers. Adds the product and refreshes the drawer in
   * place (no popup, no page reload).
   */
  document.addEventListener('click', async (evt) => {
    const btn = evt.target.closest('.js-cro-add');
    if (!btn) return;

    const cartDrawer = btn.closest('cart-drawer');
    if (!cartDrawer) return;

    evt.preventDefault();
    if (btn.getAttribute('aria-disabled') === 'true') return;

    btn.setAttribute('aria-disabled', 'true');
    btn.classList.add('is-loading');

    const sectionId = cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '');
    const variantId = Number(btn.dataset.variantId);

    try {
      const response = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: [sectionId, 'cart-icon-bubble'],
          sections_url: window.location.pathname
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || response.status);

      await cartDrawer.renderContents(data, false);

      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && data.sections['cart-icon-bubble']) {
        cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
      }

      const firstItem = cartDrawer.querySelector('.js-item-name');
      if (typeof trapFocus === 'function') trapFocus(cartDrawer, firstItem || undefined);

      cartDrawer.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: { variantId }
      }));
    } catch (error) {
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('is-loading');
      cartDrawer.dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        detail: { error: error.message }
      }));
      const message = error.message && !/^[0-9]+$/.test(error.message) ? error.message : theme.strings.cartError;
      const errorsEl = cartDrawer.querySelector('#cart-errors') || cartDrawer.querySelector('.js-cro-errors');
      if (errorsEl) {
        errorsEl.textContent = message;
        errorsEl.hidden = false;
      }
    }
  });
})();
